/**
 * Router and providers — docs/08-frontend-and-ux.md §2.1.
 *
 * `/login`, `/invite/:token` and `/reset-password` are the only routes outside the
 * authenticated shell; everything else redirects to `/login?next=…` when there is
 * no session, which is the SPA half of "no anonymous access" (docs/09 §3.1).
 */

import { Navigate, Route, Routes, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { AppShell } from './AppShell.js';
import { useSession } from './session.js';
import { LoginPage } from '../features/auth/LoginPage.js';
import { AcceptInvitePage } from '../features/auth/AcceptInvitePage.js';
import { ResetPasswordPage } from '../features/auth/ResetPasswordPage.js';
import { HomePage } from '../features/home/HomePage.js';
import { PeopleListPage } from '../features/people/PeopleListPage.js';
import { PersonProfilePage } from '../features/people/PersonProfilePage.js';
import { PersonEditorPage } from '../features/people/PersonEditorPage.js';
import { RelationEditorPage } from '../features/people/RelationEditorPage.js';
import { AdminSettingsPage } from '../features/admin/SettingsPage.js';
import { AuditLogPage } from '../features/admin/AuditLogPage.js';
import { ProposalsInboxPage } from '../features/proposals/ProposalsInboxPage.js';
import { ProposalDetailPage } from '../features/proposals/ProposalDetailPage.js';
import { CouncilPage } from '../features/council/CouncilPage.js';
import { KinshipFinderPage } from '../features/kinship/KinshipFinderPage.js';
import { PhotoUploadPage } from '../features/photos/PhotoUploadPage.js';
import { TreePage } from '../features/tree/TreePage.js';
import { NotificationsPage } from '../features/notifications/NotificationsPage.js';
import styles from '../ui/components.module.css';

function RequireSession(): JSX.Element {
  const { user, isResolved } = useSession();
  const location = useLocation();

  if (!isResolved) {
    return (
      <main className={styles.centeredPage}>
        <p className={styles.muted}>…</p>
      </main>
    );
  }

  if (!user) {
    const next = encodeURIComponent(`${location.pathname}${location.search}`);
    return <Navigate to={`/login?next=${next}`} replace />;
  }

  return <AppShell />;
}

function NotFoundPage(): JSX.Element {
  const { t } = useTranslation();
  return (
    <div>
      <h1>{t('notFound.title')}</h1>
      <p className={styles.muted}>{t('notFound.body')}</p>
      <a href="/">{t('notFound.home')}</a>
    </div>
  );
}

export function App(): JSX.Element {
  return (
    <Routes>
      <Route path="/login" element={<LoginPage />} />
      <Route path="/invite/:token" element={<AcceptInvitePage />} />
      <Route path="/reset-password" element={<ResetPasswordPage />} />

      <Route element={<RequireSession />}>
        <Route path="/" element={<HomePage />} />
        <Route path="/people" element={<PeopleListPage />} />
        {/* `new` before `:id`, or the editor would be read as a person id. */}
        <Route path="/people/new" element={<PersonEditorPage />} />
        <Route path="/people/:id" element={<PersonProfilePage />} />
        <Route path="/people/:id/edit" element={<PersonEditorPage />} />
        <Route path="/relations/new" element={<RelationEditorPage />} />
        <Route path="/kinship" element={<KinshipFinderPage />} />
        <Route path="/photos/upload" element={<PhotoUploadPage />} />
        {/* `/tree` with no pivot is a prompt to choose one, not an error. */}
        <Route path="/tree" element={<TreePage />} />
        <Route path="/tree/:id" element={<TreePage />} />
        <Route path="/proposals" element={<ProposalsInboxPage />} />
        <Route path="/proposals/:id" element={<ProposalDetailPage />} />
        <Route path="/notifications" element={<NotificationsPage />} />
        {/* Admin surfaces. The api is the authority — these render the 403 as the
            contextual explainer of docs/08 §8 rather than hiding the route. */}
        <Route path="/council" element={<CouncilPage />} />
        <Route path="/admin/audit" element={<AuditLogPage />} />
        <Route path="/admin/settings" element={<AdminSettingsPage />} />
        <Route path="*" element={<NotFoundPage />} />
      </Route>
    </Routes>
  );
}
